import type { ButtonTheme } from "./types";

export const buttonTheme: ButtonTheme = {
  base: "group flex items-center justify-center text-center font-medium focus:z-10 ",
  animate: "transition-all duration-100",
  padded: "p-0.5",
  fullSized: "w-full",
  focusRing: "focus:ring-2",
  color: {
    primary: {
      base: "text-[--contrastText] focus:ring-[--color300] dark:focus:ring-[--color800]",
      background:
        "bg-[--color500] enabled:hover:bg-[--color700] dark:bg-[--color500] dark:enabled:hover:bg-[--color700]",
      border: "border border-transparent",
    },
    // Duplicates primary for now, since we don't have a design for a non-outline message button
    message: {
      base: "text-[--contrastText] focus:ring-[--color300] dark:focus:ring-[--color800]",
      background:
        "bg-[--color500] enabled:hover:bg-[--color700] dark:bg-[--color500] dark:enabled:hover:bg-[--color700]",
      border: "border border-transparent",
    },
    secondary: {
      base: "text-gray-700 focus:outline-none hover:text-[--color700] focus:ring-gray-200 dark:focus:ring-gray-700 dark:text-gray-400 dark:hover:text-[--contrastText]",
      background: "bg-white hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700",
      border: "rounded-lg border border-gray-200 dark:border-gray-600 ",
    },
    success: {
      base: "text-[--contrastText] focus:ring-green-300 dark:focus:ring-green-800",
      background:
        "bg-green-700 enabled:hover:bg-green-800 dark:bg-green-600 dark:enabled:hover:bg-green-700",
      border: "border border-transparent",
    },
    failure: {
      base: "text-[--contrastText] focus:ring-red-300 dark:focus:ring-red-900",
      background:
        "bg-red-700 enabled:hover:bg-red-800 dark:bg-red-600 dark:enabled:hover:bg-red-700 ",
      border: "border border-transparent",
    },
    warning: {
      base: "text-[--contrastText] focus:ring-yellow-300 dark:focus:ring-yellow-900",
      background: "bg-yellow-400 enabled:hover:bg-yellow-500 ",
      border: "border border-transparent",
    },
    gray: {
      base: "text-[--contrastText] focus:ring-gray-300 dark:focus:ring-gray-900",
      background: "bg-gray-700 enabled:hover:bg-gray-500 ",
      border: "border border-transparent",
    },
  },

  disabled:
    "cursor-not-allowed bg-gray-300 border-gray-300 dark:bg-gray-500 dark:border-gray-500 pointer-events-none",
  isProcessing: "cursor-wait bg-gray-300 border-gray-300",
  spinnerSlot: "absolute h-full top-0 flex items-center animate-fade-in",
  spinnerLeftPosition: {
    xs: "left-2",
    sm: "left-3",
    md: "left-4",
    lg: "left-5",
    xl: "left-6",
  },
  inner: {
    base: "flex items-center",
    position: {
      none: "",
      start: "rounded-r-none",
      middle: "rounded-none",
      end: "rounded-l-none",
    },
    outline: "border border-transparent",
    isProcessingPadding: {
      xs: "pl-6",
      sm: "pl-8",
      md: "pl-10",
      lg: "pl-12",
      xl: "pl-14",
    },
  },
  label:
    "ml-2 inline-flex h-4 w-4 items-center justify-center rounded-full bg-cyan-200 text-xs font-semibold text-cyan-800",
  outline: {
    color: {
      primary:
        "text-[--color500] bg-white hover:text-[--contrastText] hover:bg-[--color700] border-[1.5px] border-[--color500] hover:border-[--color700] dark:bg-gray-800 dark:border-[--color700] dark:hover:bg-[--color500] dark:hover:border-[--color500] dark:text-gray-200",
      message:
        "text-[--color600] bg-white hover:bg-[--color50] border-[1.5px] border-[--color500] hover:border-[--color500] dark:bg-gray-800 dark:border-[--color700] dark:hover:bg-[--color500] dark:hover:border-[--color700] dark:text-gray-200",
      secondary:
        "text-gray-900 bg-white hover:text-[--contrastText] hover:bg-gray-900 border-[1.5px] border-gray-700 hover:border-gray-900",
      success:
        "text-green-700 bg-white hover:text-[--contrastText] hover:bg-green-700 border-[1.5px] border-green-700 hover:border-green-700",
      failure:
        "text-red-700 bg-white hover:text-[--contrastText] hover:bg-red-700 border-[1.5px] border-red-700 hover:border-red-700",
      warning:
        "text-yellow-400 bg-white hover:text-[--contrastText] hover:bg-yellow-400 border-[1.5px] border-yellow-400 hover:border-yellow-400",
      gray: "text-gray-500 bg-white hover:text-[--contrastText] hover:bg-gray-500 border-[1.5px] border-gray-500 hover:border-gray-500",
    },
    off: "",
    on: "",
    pill: {
      off: "rounded-md",
      on: "rounded-full",
    },
  },
  pill: {
    off: "rounded-lg",
    on: "rounded-full",
  },
  size: {
    xs: "text-xs px-2 py-1 gap-1 font-semibold",
    sm: "text-sm px-3 py-1.5 gap-2 leading-[18px] font-semibold",
    md: "text-base px-4 py-2 gap-2 leading-[18px] font-semibold",
    lg: "text-base px-5 py-2.5 gap-3 font-semibold",
    xl: "text-base px-6 py-3 gap-3 font-semibold",
  },
  iconButton: {
    size: {
      xs: "text-xs p-1 w-[22px] h-[22px] min-w-[22px] max-w-[22px]",
      sm: "text-sm p-1 w-[26px] h-[26px] min-w-[26px] max-w-[26px] min-h-[26px] max-h-[26px]",
      md: "text-base p-1 w-[26px] h-[26px] min-w-[26px] max-w-[26px] min-h-[26px] max-h-[26px]",
      lg: "text-base p-1 w-[26px] h-[26px] min-w-[26px] max-w-[26px] min-h-[26px] max-h-[26px]",
      xl: "text-base p-1 w-[26px] h-[26px] min-w-[26px] max-w-[26px] min-h-[26px] max-h-[26px]",
    },
  },
};
